/**
 * Variety-aware pool shaping.
 *
 * Inserts between buildEligiblePool and greedySelectHighPriority to:
 *   1. Compute sessionSaturationScore
 *   2. Augment pool with min+1 alternates when saturation is elevated
 *   3. Rank all pool units by composite fairness + variety score
 *
 * No exclusion. All eligible players remain in the shaped pool.
 * Min-games players still rank above min+1 unless their exposure is very high.
 *
 * Gated by MATCHMAKING_CONFIG.featureFlags.varietyAwarePoolSelection (default false).
 * Legacy-only when featureFlags.scoringFirstMatchmaking is true (variety moves to scoring).
 */

import { computeSessionSaturationScore, marginalVarietyGainForUnit } from './sessionVarietyMetrics.js';
import { getUnitAverageGames } from './poolBuilder.js';
import { buildUnitsFromPlayers, getUnitPlayers } from './unitBuilder.js';
import { MATCHMAKING_CONFIG } from './matchmakingConfig.js';
import {
  computeTierMedians,
  computeUnitCompositeScore,
  isSessionDebtModelEnabled,
} from './sessionDebt.js';
import { filterMatchmakingEligiblePlayerIds } from './matchmakingEligibility.js';

/** @deprecated Use MATCHMAKING_CONFIG.variety.watchThreshold when session debt is enabled */
const SATURATION_WATCH_THRESHOLD = 0.25;

/** @deprecated Use MATCHMAKING_CONFIG.variety.saturatedThreshold when session debt is enabled */
export const SATURATION_HIGH_THRESHOLD = 0.45;

const DEFAULT_SHAPING_CONFIG = {
  fairnessWeight: 0.6,
  varietyWeight: 0.4,
  pairSaturationWeight: 0.6,
  repeatQuartetWeight: 0.4,
};

/**
 * Fairness score for a unit (0–1, 1 = most fair / at min games).
 * @param {Object}   unit
 * @param {Object}   playersById
 * @param {number}   minGames
 * @returns {number}
 */
function unitFairnessScore(unit, playersById, minGames) {
  const unitGames = getUnitAverageGames(unit, playersById);
  const gap = unitGames - minGames;
  if (gap <= 0) return 1.0;
  if (gap <= 1) return 0.7;
  return 0.4;
}

/**
 * Build units for players at exactly `targetGames` who are NOT already in the pool.
 * @param {Set<string>} inPoolSet        - player IDs already in pool
 * @param {string[]}    remainingPlayers - all players not yet court-assigned
 * @param {Object}      playersById
 * @param {Array}       completePartnerships
 * @param {number}      targetGames
 * @returns {Object[]} new units
 */
function buildUnitsAtGameCount(inPoolSet, remainingPlayers, playersById, completePartnerships, targetGames) {
  const candidates = remainingPlayers.filter(id => {
    if (inPoolSet.has(id)) return false;
    const p = playersById[id];
    const games = p?.effectiveGamesPlayed ?? p?.gamesPlayedThisSession ?? 0;
    return games === targetGames;
  });
  if (candidates.length === 0) return [];
  return buildUnitsFromPlayers(candidates, completePartnerships);
}

/**
 * Shape the eligible pool for variety before greedy selection.
 *
 * @param {Object}   poolResult           - result from buildEligiblePool
 * @param {Object}   sessionState         - { playerInteractions, matchHistory, gamesPlayedThisSession }
 * @param {Array}    partnerships         - partnership objects (for saturation + variety gain)
 * @param {string[]} remainingPlayers     - all players not yet assigned to a court this round
 * @param {Object}   playersById          - map of player objects by ID
 * @param {Array}    completePartnerships - complete partnerships for unit building
 * @param {Object}   [varietyConfig={}]   - weight overrides from MATCHMAKING_CONFIG.variety
 * @param {boolean}  [expandToMinPlusTwo=false] - Phase 3: also include min+2 units
 * @param {Object}   [playerWaitMetrics={}]    - Wait metrics for session debt ranking
 * @returns {{
 *   shapedPool: Object[],
 *   shapingApplied: boolean,
 *   shapingReason: string,
 *   saturation: { score: number, pairSaturation: number, repeatQuartetRate: number },
 *   unitRanks: Array<{ unitId, playerIds, fairnessScore, varietyScore, compositeScore }>
 * }}
 */
export function shapePoolForVariety(
  poolResult,
  sessionState,
  partnerships,
  remainingPlayers,
  playersById,
  completePartnerships,
  varietyConfig = {},
  expandToMinPlusTwo = false,
  playerWaitMetrics = {},
  sessionPlayerStatus = null,
) {
  const config = { ...DEFAULT_SHAPING_CONFIG, ...varietyConfig };
  const watchThreshold = config.watchThreshold ?? MATCHMAKING_CONFIG.variety?.watchThreshold ?? SATURATION_WATCH_THRESHOLD;
  const saturatedThreshold = config.saturatedThreshold ?? MATCHMAKING_CONFIG.variety?.saturatedThreshold ?? SATURATION_HIGH_THRESHOLD;
  const poolUnits = poolResult.pool || [];
  const minGames = poolResult.minGames ?? 0;

  const eligibleRemaining = sessionPlayerStatus
    ? filterMatchmakingEligiblePlayerIds(remainingPlayers, sessionPlayerStatus)
    : remainingPlayers;

  // Active matchmaking player IDs (for exposure / saturation denominators)
  const allSessionPlayerIds = eligibleRemaining.length > 0
    ? eligibleRemaining
    : filterMatchmakingEligiblePlayerIds(
      Object.keys(sessionState?.gamesPlayedThisSession || {}),
      sessionPlayerStatus || {},
    );

  // Compute session saturation
  const saturation = computeSessionSaturationScore(
    sessionState,
    allSessionPlayerIds,
    partnerships,
    {
      pairSaturationWeight: config.pairSaturationWeight,
      repeatQuartetWeight: config.repeatQuartetWeight,
    },
  );

  const { score } = saturation;
  let workingPool = [...poolUnits];
  let shapingApplied = false;
  let shapingReason = 'healthy';

  if (score >= watchThreshold) {
    const inPoolSet = new Set(workingPool.flatMap(u => getUnitPlayers(u)));

    // Add min+1 alternates
    const minPlusOneUnits = buildUnitsAtGameCount(
      inPoolSet, eligibleRemaining, playersById, completePartnerships, minGames + 1,
    );
    if (minPlusOneUnits.length > 0) {
      workingPool = [...workingPool, ...minPlusOneUnits];
      shapingApplied = true;
    }

    // Phase 3: also add min+2 when saturation is high
    if (expandToMinPlusTwo || score >= saturatedThreshold) {
      const updatedInPoolSet = new Set(workingPool.flatMap(u => getUnitPlayers(u)));
      const minPlusTwoUnits = buildUnitsAtGameCount(
        updatedInPoolSet, eligibleRemaining, playersById, completePartnerships, minGames + 2,
      );
      if (minPlusTwoUnits.length > 0) {
        workingPool = [...workingPool, ...minPlusTwoUnits];
        shapingApplied = true;
      }
    }

    if (shapingApplied) {
      shapingReason = score >= saturatedThreshold
        ? `saturated (${score.toFixed(2)}) — pool expanded to min+1/min+2`
        : `watch (${score.toFixed(2)}) — min+1 alternates included`;
    }
  }

  const tierMedians = isSessionDebtModelEnabled()
    ? computeTierMedians(eligibleRemaining, playersById, playerWaitMetrics)
    : new Map();

  // Rank all units: composite = fairnessWeight * fairness + varietyWeight * variety
  // When session debt is on, fairness uses relative wait + pacing signals.
  const unitRanks = workingPool.map(unit => {
    const playerIds = getUnitPlayers(unit);
    const fairnessScore = isSessionDebtModelEnabled()
      ? computeUnitCompositeScore(
        unit,
        playersById,
        playerWaitMetrics,
        tierMedians,
        minGames,
        sessionState,
        completePartnerships,
        0,
      )
      : unitFairnessScore(unit, playersById, minGames);
    const varietyScore = marginalVarietyGainForUnit(
      playerIds,
      [],
      allSessionPlayerIds,
      sessionState,
      completePartnerships,
    );
    const compositeScore = isSessionDebtModelEnabled()
      ? fairnessScore + config.varietyWeight * varietyScore
      : config.fairnessWeight * fairnessScore + config.varietyWeight * varietyScore;
    return { unit, playerIds, fairnessScore, varietyScore, compositeScore };
  });

  // Stable descending sort: higher composite = picked first
  unitRanks.sort((a, b) => b.compositeScore - a.compositeScore);

  return {
    shapedPool: unitRanks.map(r => r.unit),
    shapingApplied,
    shapingReason,
    saturation,
    unitRanks: unitRanks.map(r => ({
      unitId: r.unit.id,
      playerIds: r.playerIds,
      fairnessScore: r.fairnessScore,
      varietyScore: r.varietyScore,
      compositeScore: r.compositeScore,
    })),
  };
}
